/* @flow */
/* eslint-env es6,browser */

import _idpbase from './idpbase';
import { IDP_ID5 } from './idpconstants';
import isdebugEnabled from '../utils/debug';
import {
  BrowserUtil,
  getHostname,
  getPageUrl,
  loadJSWithTimeout
} from '../utils/browserutil';
import getCVars from '../utils/gputils';
import { param } from '../utils/coreutils';
import { logDebug } from '../utils/logger';

const SCRIPT_LOAD_TIMEOUT_MSG = 5000; // long wait is OK here as we are waiting only once here
const MIN_REFRESH_INTERVAL_MINUTES = 2 * 60; // 2 hours as per ID5 default

/**
 * Using the CDN version of ID5 JSAPI here.
 * Read https://github.com/id5io/id5-api.js#id5-cdn for more info
 */

class _id5idp extends _idpbase {
  #bInited: boolean;

  #bScriptLoaded: boolean;

  #id5status: anyObject | null;

  constructor(name: string) {
    super(name);
    this.#bInited = false;
    this.#bScriptLoaded = false;
    this.#id5status = null;
  }

  _loadScript() {
    if (
      this.#bScriptLoaded ||
      !this._url ||
      this._url.length === 0 ||
      this._pid.length === 0
    ) {
      return;
    }

    // set ID for Google secure signals
    // Refer https://wiki.id5.io/en/identitycloud/retrieve-id5-ids/google-secure-signals
    const win = this._win;
    win.ID5EspConfig = win.ID5EspConfig || {};
    win.ID5EspConfig.partnerId = this._pid;

    // Full url along with the Publisher/profile ID comes from config
    loadJSWithTimeout(this._url, this._doc, true, SCRIPT_LOAD_TIMEOUT_MSG, {
      object: 'ID5'
    }).then(
      () => {
        // Let's initialize
        this._refreshID();
        return true;
      },
      error => {
        logDebug(`${this._name} failed to load ${this._url}: ${error}`);
      }
    );
    this.#bScriptLoaded = true;
  }

  _updateConfig(o: perIDPConfig) {
    super._updateConfig(o);
    this._loadScript();
  }

  _shouldRefresh(): boolean {
    return !this.#bInited;
  }

  _refreshID() {
    const { ID5 } = this._win;

    const partnerId = this._pid || '';
    if (
      partnerId.length === 0 ||
      !this.#bScriptLoaded ||
      this.#bInited ||
      !ID5
    ) {
      // nothing further to do here
      return;
    }
    // all the values in privacy data string should be
    // encoded except sha256 and then btoa encoded.
    // Refer https://wiki.id5.io/en/identitycloud/retrieve-id5-ids/passing-partner-data-to-id5
    const { _uh6: sha256Email = '' } = getCVars();
    const ua = BrowserUtil.getUserAgent();
    const pageUrl = getPageUrl();
    const domain = getHostname();
    const pdKeys = {
      '1': sha256Email,
      '12': ua,
      '8': pageUrl,
      '9': domain
    };
    // eslint-disable-next-line compat/compat
    const pd = btoa(param(pdKeys));

    const refreshInSeconds =
      Math.max(this._refreshInterval, MIN_REFRESH_INTERVAL_MINUTES) * 60; // convert to seconds
    const storageExpirationDays = this._nExpireDays;
    const o = {
      partnerId,
      pd,
      refreshInSeconds,
      storageExpirationDays
    };
    ID5.debug = isdebugEnabled();
    ID5.init(o).onAvailable(status => {
      this.#id5status = status;
    });
    this.#bInited = true;
  }

  _isID5Ready(): boolean {
    const { ID5 } = this._win;
    return this.#bInited && ID5 && ID5.loaded && !!this.#id5status;
  }

  getID(): string {
    let ret = '';
    if (!this._isID5Ready()) {
      return ret;
    }
    // $FlowFixMe[incompatible-use]
    ret = this.#id5status.getUserId() || ret;
    return ret;
  }

  getExt(): anyObject {
    let ret = {};
    if (!this._isID5Ready()) {
      return ret;
    }
    // $FlowFixMe[incompatible-use]
    ret = this.#id5status.getExt() || ret;
    return ret;
  }
}

const id5idp: _id5idp = new _id5idp(IDP_ID5);
export default id5idp;
